import { MenuItem, useMediaQuery } from '@mui/material';
import { Theme } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import React, { useCallback, useEffect, useLayoutEffect, useMemo } from 'react';
import { GridColDef, GridRowParams, useGridApiRef } from '@mui/x-data-grid-premium';
import { useTranslation } from 'react-i18next';
import { Dialogs } from '../../constants/Dialogs';
import { openDialog } from '../../ducks/dialogSlice';
import { Dealer } from '../../types/Dealer';
import { SortableProperty } from '../../types/SortableProperty';
import { SortProperty } from '../../types/SortProperty';
import { formatPhoneNumber } from '../../utils/phoneNumberUtils';
import { sortRows } from '../../utils/sortUtils';
import { stringToLowerIncludes } from '../../utils/stringUtils';
import { DealerDialog } from './DealerDialog';
import { I18nKeys } from '../../constants/I18nKeys';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { MuiDataGrid } from '../MUIDataGrid';
import { dealersColumns, MUIDataGrid, MUIDataGridColumn } from '../../constants/MUIDataGrid';
import { getDefaultColumnDefinition } from '../../utils/MUIDataGridUtils';
import { setSecondaryActions } from '../../ducks/secondaryActions';
import { fetchDealers, setDealerDialog } from '../../ducks/dealersSlice';
import { Fab } from '../library/Fab';
import { PageHeader } from '../library/PageHeader';
import { TableSortMenu } from '../TableSortMenu';
import { DealersCards } from './DealersCards';
import { Page } from '../library/Page';

export const Dealers: React.FC = () => {
  const dispatch = useAppDispatch();
  const apiRef = useGridApiRef();
  const { t } = useTranslation();

  const { dealers, loading, logoUrl } = useAppSelector((state) => state.dealers);
  const searchTerm = useAppSelector((state) => state.search.searchTerm);
  const selectedClientId = useAppSelector((state) => state.viewer.selectedClientId || '');

  const mobileView = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const [filteredDealers, setFilteredDealers] = React.useState<Dealer[]>(dealers);
  const [sortState, setSortState] = React.useState<SortProperty[]>([]);

  const handleExport = useCallback(() => {
    apiRef?.current?.exportDataAsCsv({ fileName: t(I18nKeys.DealerExportCSVTitle) });
  }, [apiRef]);

  useLayoutEffect(() => {
    dispatch(
      setSecondaryActions([
        <MenuItem key="export" onClick={handleExport}>
          {t(I18nKeys.ExportButton)}
        </MenuItem>,
      ]),
    );
  }, [dispatch, t, handleExport]);

  useEffect(() => {
    // Don't bother searching until there's at least 3 characters
    if (searchTerm.length < 3) {
      setFilteredDealers(dealers);
      return;
    }
    const tests = [
      (dealer: Dealer): boolean => stringToLowerIncludes(dealer.name, searchTerm),
      (dealer: Dealer): boolean => stringToLowerIncludes(dealer.key, searchTerm),
      (dealer: Dealer): boolean => stringToLowerIncludes(dealer.city, searchTerm),
      (dealer: Dealer): boolean => stringToLowerIncludes(dealer.state, searchTerm),
      (dealer: Dealer): boolean => stringToLowerIncludes(dealer.zip, searchTerm),
      (dealer: Dealer): boolean => stringToLowerIncludes(formatPhoneNumber(dealer.phoneNumber), searchTerm),
      (dealer: Dealer): boolean => stringToLowerIncludes(dealer.phoneNumber, searchTerm),
      (dealer: Dealer): boolean => stringToLowerIncludes(dealer.emailAddress, searchTerm),
    ];

    setFilteredDealers(dealers.filter((dealer: Dealer) => tests.some((test) => test(dealer))));
  }, [dealers, filteredDealers.length, selectedClientId, searchTerm]);

  useEffect(() => {
    if (selectedClientId) {
      dispatch(fetchDealers(selectedClientId));
    }
  }, [selectedClientId, dispatch]);

  const handleSortChanged = useCallback(
    (sortProperties: SortProperty[]) => {
      setSortState(sortProperties);
      sortRows(filteredDealers, sortProperties);
    },
    [filteredDealers],
  );

  const onRowClick = React.useCallback(
    (params: GridRowParams<any>) => {
      const { row: dealer } = params;
      dispatch(setDealerDialog({ dialogDealer: dealer, dialogClientId: selectedClientId }));
      dispatch(openDialog({ dialog: Dialogs.Dealer }));
    },
    [selectedClientId, dispatch],
  );

  const sortableProperties: SortableProperty[] = [
    { i18nKey: I18nKeys.TableHeaderName, property: 'name' },
    { i18nKey: I18nKeys.TableHeaderCity, property: 'city' },
    { i18nKey: I18nKeys.TableHeaderState, property: 'state' },
    { i18nKey: I18nKeys.TableHeaderPhone, property: 'phoneNumber' },
    { i18nKey: I18nKeys.TableHeaderContactEmail, property: 'emailAddress' },
  ];

  const columns: GridColDef[] = useMemo(
    () =>
      dealersColumns.map((column: MUIDataGridColumn) =>
        getDefaultColumnDefinition(
          { table: MUIDataGrid.Dealers, clientId: selectedClientId, editable: false },
          column,
          {},
          dispatch,
          t,
        ),
      ),
    [t, dispatch, selectedClientId],
  );

  return (
    <Page sx={{ height: '100%', width: '100%' }}>
      {!mobileView && (
        <MuiDataGrid
          header="Dealers"
          loading={loading}
          rows={filteredDealers}
          columns={columns}
          apiRef={apiRef}
          getRowId={({ id }) => id}
          onRowClick={onRowClick}
        />
      )}
      {mobileView && (
        <PageHeader
          id="dealers-header"
          header="Dealers"
          count={filteredDealers.length}
          loading={loading}
          buttons={[
            <TableSortMenu
              sortProperties={sortState}
              sortableProperties={sortableProperties}
              onSort={handleSortChanged}
            />,
          ]}
        />
      )}
      {mobileView && <DealersCards filteredDealers={filteredDealers} />}
      {!loading && (
        <Fab
          id="dealers-add-fab"
          icon={<AddIcon />}
          label={t(I18nKeys.AddButton)}
          onClick={(): void => {
            dispatch(setDealerDialog({ dialogDealer: undefined, dialogClientId: selectedClientId, logoUrl }));
            dispatch(openDialog({ dialog: Dialogs.Dealer }));
          }}
        />
      )}
      <DealerDialog />
    </Page>
  );
};
